import { Component, ChangeDetectorRef, computed, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../core/services/api.service';
import { PaginatedTableComponent, TableHeader, TableActionOptions } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { ToasterService } from '../../../../core/services/toaster.service';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { UserFormPanelComponent } from '../components/user-form-panel/user-form-panel.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../../core/models/audit-log.model';
import { ProductConfigService } from '../../../../core/services/product-config.service';

@Component({
  selector: 'app-access-request-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownComponent,
    PaginatedTableComponent,
    ButtonComponent,
    IconComponent,
    UserFormPanelComponent,
    AuditLogPanelComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './access-request-logs.component.html'
})
export class AccessRequestLogsComponent implements OnInit {
  private apiservice = inject(ApiService);
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private productConfigService = inject(ProductConfigService);

  @ViewChild(UserFormPanelComponent) userFormPanel!: UserFormPanelComponent;
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;

  isLoading = signal(false);
  totalItems = signal(0);
  auditEntityTypes = [AuditEntityType.REQUEST_ACCESS];
  
  // Filters
  searchTerm = signal('');
  selectedStatus = signal<string | null>(null);
  page = signal(1);
  pageSize = signal(10);
  logsData = signal<any[]>([]);

  statusOptions: DropdownOption[] = [
    { value: '', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  tableHeaders: TableHeader[] = [
    { label: 'Name', key: 'fullName', align: 'text-left' },
    { label: 'Email', key: 'email', align: 'text-left' },
    { label: 'Reason', key: 'reason', align: 'text-left' },
    { label: 'Products', key: 'productNames', align: 'text-left' },
    { label: 'Approval Reason', key: 'note', align: 'text-left'},
    { label: 'Status', key: 'status', align: 'text-left', cellType: 'badge' },
    { label: 'Date', key: 'createdAt', align: 'text-left', dateFormat: 'mediumDate' }
  ];

  tableActionOptions: TableActionOptions = {
    showToggleButton: true,
    columnLabel: 'Actions',
    useIconV2: true,
    toggleOptions: [
      {
        iconName: 'circleCheck',
        color: '#10B981',
        isShow: true,
        value: 'approve',
        visibilityKey: 'showActions',
        tooltip: 'Approve'
      },
      {
        iconName: 'circleX',
        color: '#EF4444',
        isShow: true,
        value: 'reject',
        visibilityKey: 'showActions',
        tooltip: 'Reject'
      }
    ]
  };

  get processedLogsData() {
    return this.logsData().map((log) => ({
      ...log,
      fullName: `${log.firstName} ${log.lastName}`,
      productNames: log.products?.map((p: any) => p.name).join(', ') || 'N/A',
      showActions: log.status === 'PENDING',
      status_class: log.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' : 
                   log.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' : 
                   'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      productTags: log.products?.map((p: any) => {
        const theme = this.productConfigService.getProductConfig(p.name)?.chatbotTheme;
        return {
          label: p.name,
          color: theme 
            ? `${theme.primaryBg} ${theme.primaryText} border ${theme.primaryBorder}`
            : 'bg-primary-light text-primary-dark dark:bg-primary-dark dark:text-primary-light'
        };
      }) || [],
      note: log.note || 'N/A'
    }));
  }

  ngOnInit() {
    this.fetchLogs();
  }

  fetchLogs(): void {
    this.isLoading.set(true);
    const params: any = {
      page: this.page(),
      limit: this.pageSize(),
    };

    const status = this.selectedStatus();
    const search = this.searchTerm();

    if (status) params.status = status;
    if (search) params.search = search;

    this.apiservice.get('auth/access-request/list', params, {
      showErrorToaster: true,
      showLoader: false,
    }).subscribe({
      next: (response: any) => {
        // The API returns { data: { success: true, data: [...], total: 2 } }
        const items = response?.data || [];
        const total = response?.total || items.length;
        
        this.logsData.set(items);
        this.totalItems.set(total);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toasterService.error('Failed to load access request logs');
      }
    });
  }

  onStatusFilter(status: string | string[] | null): void {
    const selected = Array.isArray(status) ? status[0] : status;
    this.selectedStatus.set(selected === '' ? null : selected);
    // this.applyFilters();
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
  }

  applyFilters() {
    this.page.set(1);
    this.fetchLogs();
  }

  clearFilters() {
    this.selectedStatus.set(null);
    this.searchTerm.set('');
    this.page.set(1);
    this.fetchLogs();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetchLogs();
  }

  handleUserSaved() {
    this.fetchLogs();
    this.auditLogPanel?.reloadLogs();
  }

  onActionSelected(event: { action: string; row: any }) {
    if (event.action === 'approve') {
      this.userFormPanel.openPanel(null, {
        email: event.row.email,
        firstName: event.row.firstName,
        lastName: event.row.lastName,
        productIds: event.row.products?.map((p: any) => p.pid || p.id) || []
      }, true);
    } else if (event.action === 'reject') {
      this.dialog
        .open(ConfirmationModalComponent, {
          width: '400px',
          data: {
            title: 'Reject Request',
            description: `Are you sure you want to reject the access request for ${event.row.firstName} ${event.row.lastName}?`,
            confirmLabel: 'Reject',
            showInput: true,
            inputRequired: true,
            inputPlaceholder: 'Add a reason for rejection (required)',
          },
        })
        .afterClosed()
        .subscribe((res) => {
          if (res?.confirmed) {
            const note = res.note;
            this.handleReject(event.row.id, note);
          }
        });
    }
  }

  handleReject(id: string, note?: string) {
    this.apiservice
      .patch(`auth/access-request/${id}/status`, { note: note }, {
        showErrorToaster: true,
        showSuccessToaster: true,
        showLoader: true,
      })
      .subscribe({
        next: () => {
          this.fetchLogs();
          this.auditLogPanel?.reloadLogs();
        },
      });
  }
}
